import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * useScrollReveal — wires a single IntersectionObserver to all elements with
 * `data-reveal` attributes. When an element enters the viewport, the observer
 * toggles `data-reveal-visible="true"` on it which triggers the CSS transition
 * defined in index.css.
 *
 * The hook re-scans on route changes so new content after navigation is also
 * observed.
 */
export default function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // Old browsers: just reveal everything immediately.
      document
        .querySelectorAll("[data-reveal]")
        .forEach((el) => el.setAttribute("data-reveal-visible", "true"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute("data-reveal-visible", "true");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );

    // Allow the DOM to settle after route changes / lazy renders before observing.
    const id = window.setTimeout(() => {
      document.querySelectorAll("[data-reveal]:not([data-reveal-visible])").forEach((el) => {
        io.observe(el);
      });
    }, 60);

    return () => {
      window.clearTimeout(id);
      io.disconnect();
    };
  }, [pathname]);
}
